import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { logoutUser } from "../../store/slices/authSlice";
import logo from "../../assets/images/logo.png";
import notification_icon from "../../assets/images/notification_icon.svg";
import circleUser from "../../assets/images/circle-user.svg";
import userLogout from "../../assets/images/user-logout.svg";
import passwordIcon from "../../assets/images/password.svg";
import user_img from "../../assets/images/user_img.png";
import {
  fetchAllNotifications,
  selectNotificationList,
} from "../../store/slices/notificationSlice";
import {
  startNotificationConnection,
  stopNotificationConnection,
  getNotificationConnection,
} from "../../services/startNotificationConnection";
import { formatDate } from "../../utils/dateUtils";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { role, user } = useAppSelector((s) => s.user);

  const userId = user?.id;
  const companyId = user?.id;

  const NOTIFICATION_KEY = `headerNotifications_${userId}`;

  const notifications = useAppSelector((state) =>
    selectNotificationList(state, userId),
  );
  const totalRecords = useAppSelector(
    (s) => s.user.totalByKey[NOTIFICATION_KEY] || 0,
  );

  const loadTop = (id) => {
    if (!id) return;
    dispatch(
      fetchAllNotifications({
        params: { userId: id, role: role, pageIndex: 0, pageSize: 5 },
        key: NOTIFICATION_KEY,
      }),
    );
  };

  const handleLogout = async () => {
    try {
      await stopNotificationConnection();

      await dispatch(logoutUser()).unwrap();

      // Navigate only after Redux logout is completed
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);

      // Even if logout API fails, go to login
      navigate("/", { replace: true });
    }
  };

  // Define menus with companyId for Company role
  const menus = {
    SuperAdmin: [
      { label: "Dashboard", path: "/dashboard" },
      { label: "Client", path: "/client" },
      { label: "Companies", path: "/companies" },
      { label: "Formula", path: "/formula" },
      { label: "History", path: "/history" },
    ],
    Client: [
      { label: "Dashboard", path: "/dashboard" },
      { label: "Companies", path: "/companies" },
      { label: "Employee", path: "/employes" },
      { label: "History", path: "/history" },
    ],
    Company: [
      { label: "Dashboard", path: "/dashboard" },
      { label: "Employee", path: "/employes" },
      { label: "Payroll", path: `/selectedpayroll/${companyId}` },
    ],
  };

  useEffect(() => {
    const init = async () => {
      if (!userId) return;

      const connection = await startNotificationConnection(role, userId);

      loadTop(userId);

      connection.on("ReceiveNotification", () => {
        loadTop(userId);
      });

      connection.on("NotificationUpdated", () => {
        loadTop(userId);
      });
    };

    init();

    return () => {
      const connection = getNotificationConnection();
      if (connection) {
        connection.off("ReceiveNotification");
        connection.off("NotificationUpdated");
      }
    };
  }, [userId, role]);

  const activeMenu = menus[role] || menus.SuperAdmin;
  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";
  const profileLogo = user?.profile ? BASE_URL + user.profile : user_img;

  return (
    <nav className="navbar navbar-expand-lg custom_header">
      <div className="container-fluid">
        <NavLink className="navbar-brand" to="/">
          <img src={logo} alt="Logo" />
        </NavLink>

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav mx-auto">
            {activeMenu.map((item) => (
              <li className="nav-item" key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => {
                    const isDashboard =
                      item.path === "/dashboard" &&
                      (location.pathname === "/" ||
                        location.pathname === "/dashboard");

                    return (
                      "nav-link" + (isActive || isDashboard ? " active" : "")
                    );
                  }}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <ul className="header_icons">
            <li className="nav-item dropdown notifications">
              <a
                className="nav-link dropdown-toggle p-0 notification_icon_wrapper"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
              >
                <img src={notification_icon} alt="Notifications" />
                {totalRecords > 0 && (
                  <span className="notification_badge">
                    {totalRecords > 99 ? "99+" : totalRecords}
                  </span>
                )}
              </a>
              <div className="dropdown-menu dropdown-menu-end">
                <div className="d-flex mb_16">
                  <h4 className="fs_18 fw_400 mb-0">
                    Notifications{" "}
                    {totalRecords > 0 && (
                      <span className="fs_18 fw_700 mb-0">{totalRecords}</span>
                    )}
                  </h4>
                  <NavLink to="/notifications" className="btn_link ms-auto">
                    View All
                  </NavLink>
                </div>
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div
                      key={notification.notificationId}
                      className={`notification_list d-flex align-items-center ${!notification.isRead ? "bg_blue" : ""}`}
                    >
                      {/* <div className="user_profile">
                        <img src={user_img} alt="User" />
                      </div> */}
                      <div className="notification_content ml_10">
                        <h6>{notification.title}</h6>
                        <p>{notification.message}</p>
                        <p className="time">
                          {formatDate(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center p-3">No notifications</p>
                )}
              </div>
            </li>

            <Dropdown align="end">
              <Dropdown.Toggle
                variant="light"
                className="p-0 bg-transparent border-0 header_profile_toggle"
              >
                <img
                  src={profileLogo}
                  alt="User"
                  className="header_profile_img"
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item>
                  <img
                    src={profileLogo}
                    alt="User"
                    className="header_profile_img"
                    style={{
                      marginLeft: "-5px",
                      width: "30px",
                      height: "auto",
                    }}
                  />{" "}
                  {(user?.name?.length > 10
                    ? user?.name.slice(0, 10) + "..."
                    : user?.name) || "User"}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => navigate("/profilePage")}>
                  <img src={circleUser} alt="Profile" /> Profile
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => navigate("/changepassword")}>
                  <img
                    src={passwordIcon}
                    alt="Password"
                    style={{ width: "19px", marginLeft: "-3px" }}
                  />{" "}
                  Password
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item className="text-danger" onClick={handleLogout}>
                  <img src={userLogout} alt="Logout" /> Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Header;
